import { useCallback, useEffect, useMemo, useState } from "react";
import { PolicySaysChip } from "../components/PolicySaysChip";
import type React from "react";
import { useSearchParams } from "react-router-dom";
import { TicketChip, TicketControl, useTicketLinks, type TicketLinks } from "../components/TicketLink";
import { AlertTriangle, Bug, ChevronDown, ChevronRight, Clock, Upload } from "lucide-react";
import { theme, card, btnPrimary, emptyFrame, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { PortalLink } from "../components/PortalLink";
import { LoadingBlock } from "../components/Spinner";
import { DetailFields, DetailSection, RecordActions } from "../components/RecordView";
import { NextSteps } from "../components/NextSteps";
import {
  ModalOverlay, ModalHeader, Field, input, textarea, EmptyState, SeverityPill, secondaryBtn, dangerBtn,
  fmtDay, fmtDateTime, fmtDateOnly, dateInputToIso, relativeDue, type AttentionItem,
} from "./resilience/shared";
import { PlaybookBar } from "../components/PlaybookBar";

// Vulnerability management: one queue over Defender TVM and scanner imports, ordered by
// priority, with a remediation deadline per finding and risk-accepted exceptions.
// Rules in backend/src/lib/vulnerabilityPriority.ts; routes in routes/vulnerabilities.routes.ts.

type Severity = "critical" | "high" | "medium" | "low";
type Band = "P1" | "P2" | "P3" | "P4";
type Standing = "resolved" | "excepted" | "breached" | "due_soon" | "on_track" | "no_deadline";

interface Finding {
  id: string;
  assetId: string;
  asset: { id: string; name: string; kind: string; criticality: string; isEssentialFunction: boolean };
  source: string;
  scanner: string | null;
  cveId: string;
  title: string;
  productName: string;
  severity: Severity | null;
  cvss: number | null;
  kev: boolean;
  epss: number | null;
  port: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  dueAt: string | null;
  standing: Standing;
  priority: { score: number; band: Band; reasons: string[] };
  exceptionUntil: string | null;
  exceptionReason: string | null;
  remediationNote: string | null;
  fixingKbId: string | null;
  /** The risk register entry the finding is filed under, and an exception accepted by. */
  risk: { id: string; displayId: number | null; title: string } | null;
}

/** A risk an exception may rest on: an "accept" treatment naming who accepted it and when (6.1.3 f). */
interface RiskOption {
  id: string; displayId?: number | null; title: string; status: string; owner?: string | null;
  treatments: Array<{ type: string; owner?: string | null; acceptedAt?: string | null }>;
}
const riskLabel = (r: { displayId?: number | null; title: string }) => `${r.displayId != null ? `RISK-${String(r.displayId).padStart(3, "0")} ` : ""}${r.title}`;
const acceptsRisk = (r: RiskOption) => r.status !== "closed" && r.treatments.some((t) => t.type === "accept" && ((t.owner ?? "").trim() || (r.owner ?? "").trim()) && t.acceptedAt);

interface Metrics {
  open: Record<Severity, number>;
  openTotal: number;
  breached: number;
  excepted: number;
  kevOpen: number;
  withinSlaPct: number | null;
  medianDaysToResolve: Partial<Record<Severity, number>>;
}

interface ImportRecord {
  id: string;
  scanner: string;
  fileName: string;
  scannedAt: string | null;
  rows: number;
  findings: number;
  matchedHosts: number;
  unmatchedHosts: string[] | null;
  created: number;
  refreshed: number;
  resolved: number;
  createdAt: string;
}

type Sla = Record<Severity, number>;

interface VulnResponse {
  findings: Finding[];
  metrics: Metrics;
  attention: AttentionItem[];
  sla: Sla;
  ceEnrolled: boolean;
  imports: ImportRecord[];
  scanners: Record<string, string>;
  defaults: Sla;
}

const SEVERITIES: Severity[] = ["critical", "high", "medium", "low"];
const BANDS: Band[] = ["P1", "P2", "P3", "P4"];
const STANDING_FILTERS: Array<{ id: string; label: string }> = [
  { id: "breached", label: "Past deadline" },
  { id: "due_soon", label: "Due soon" },
  { id: "on_track", label: "On track" },
  { id: "excepted", label: "Risk accepted" },
];
const CE_MAX_DAYS = 14;
const MAX_FILE_BYTES = 15 * 1024 * 1024;

const BAND_STYLE: Record<Band, { bg: string; color: string }> = {
  P1: { bg: theme.color.dangerBg, color: theme.color.dangerText },
  P2: { bg: theme.color.warningBg, color: theme.color.warningText },
  P3: { bg: theme.color.accentBg, color: theme.color.accentText },
  P4: { bg: theme.color.panelAlt, color: theme.color.muted },
};

const STANDING_COLOR: Record<Standing, string> = {
  breached: theme.color.dangerText,
  due_soon: theme.color.warningText,
  on_track: theme.color.successText,
  excepted: theme.color.accentText,
  resolved: theme.color.successText,
  no_deadline: theme.color.muted,
};

const pill = (bg: string, color: string): React.CSSProperties => ({ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: bg, color, whiteSpace: "nowrap" });
const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", verticalAlign: "top" };
const headRow: React.CSSProperties = { textAlign: "left", color: theme.color.subtle, fontSize: text.label.fontSize };
const sectionTitle: React.CSSProperties = { ...text.sectionTitle, margin: 0 };
const SEV_DOT: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };

function sourceLabel(f: Finding, scanners: Record<string, string>): string {
  if (f.source === "defender") return "Defender";
  if (f.source === "scanner") return f.scanner ? scanners[f.scanner] ?? "Scanner" : "Scanner";
  return f.source;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function VulnerabilitiesPage() {
  const { user } = useCurrentUser();
  const can = (p: string) => !!user?.permissions?.includes(p);
  const [params, setParams] = useSearchParams();
  // Tickets raised in the organisation's ticketing system, shown beside each finding.
  const tickets = useTicketLinks("vulnerability");
  const [data, setData] = useState<VulnResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [severity, setSeverity] = useState<string>("");
  const [source, setSource] = useState<string>("");
  // ?cve=<id> (from a threat advisory's CVE badge) opens the page searched for that CVE.
  const [q, setQ] = useState(() => params.get("cve") ?? "");
  const [showResolved, setShowResolved] = useState(false);
  // ?finding=<id> opens that finding's read-only view.
  const expanded = params.get("finding");
  const setExpanded = (id: string | null) => setParam("finding", id ?? "");

  const [exceptionFor, setExceptionFor] = useState<Finding | null>(null);
  const [resolveFor, setResolveFor] = useState<Finding | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const band = (params.get("band") ?? "").toUpperCase();
  const sla = params.get("sla") ?? "";
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(() => {
    api.get<VulnResponse>("/vulnerabilities")
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(apiErrorMessage(e, "Could not load vulnerabilities")));
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.findings.filter((f) => {
      if (!showResolved && f.resolvedAt && f.id !== expanded) return false;
      if (band && f.priority.band !== band) return false;
      if (sla && f.standing !== sla) return false;
      if (severity && f.severity !== severity) return false;
      if (source && f.source !== source) return false;
      if (needle && !`${f.cveId} ${f.title} ${f.asset.name} ${f.productName}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [data, q, showResolved, band, sla, severity, source, expanded]);

  const removeException = async (f: Finding) => {
    if (!window.confirm(`Remove the exception on ${f.cveId}? Its deadline applies again straight away.`)) return;
    setRowError(null);
    try { await api.del(`/vulnerabilities/findings/${f.id}/exception`); load(); }
    catch (e) { setRowError(apiErrorMessage(e, "Could not remove the exception")); }
  };

  const heading = (
    <>
      <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
        <Bug size={20} /> Vulnerabilities
      </h1>
      <p style={{ ...pageSubtitle, maxWidth: 760 }}>
        Every vulnerability found on your devices, from Microsoft Defender and your scanner exports, in the order to fix them, with a deadline for each.
      </p>
      <PlaybookBar ids={["vulnerability-management"]} />
      <PolicySaysChip keys={["vulnerability.critical_days", "vulnerability.high_days", "vulnerability.medium_days", "vulnerability.low_days"]} />
    </>
  );

  if (error && !data) return <div className="page-pad">{heading}<div style={{ marginTop: 16, ...text.body, color: theme.color.dangerText }}>{error}</div></div>;
  if (!data) return <div className="page-pad">{heading}<LoadingBlock /></div>;

  const m = data.metrics;
  const openCount = data.findings.filter((f) => !f.resolvedAt).length;
  const med = (s: Severity) => (m.medianDaysToResolve[s] != null ? String(m.medianDaysToResolve[s]) : "—");

  return (
    <div className="page-pad">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>{heading}</div>
        {can("resilience:write") && (
          <button type="button" onClick={() => setImportOpen(true)} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}>
            <Upload size={14} /> Import scanner results
          </button>
        )}
      </div>

      <div style={{ display: "grid", gap: 20, marginTop: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <Tile label="Open" value={m.openTotal} sub={`${m.open.critical} critical · ${m.open.high} high · ${m.open.medium} medium · ${m.open.low} low`} />
          <Tile label="Known exploited, open" value={m.kevOpen} danger={m.kevOpen > 0} sub="attackers are using these now" onClick={() => setParam("band", "P1")} />
          <Tile label="Past deadline" value={m.breached} danger={m.breached > 0} sub={`${m.excepted} risk accepted`} onClick={() => setParam("sla", "breached")} />
          <Tile label="Fixed within deadline" value={m.withinSlaPct == null ? "—" : `${m.withinSlaPct}%`} sub="critical and high, last 12 months" />
          <Tile label="Median days to fix" value={`${med("critical")} / ${med("high")}`} sub="critical / high, last 12 months" />
        </div>

        {data.attention.length > 0 && (
          <div style={{ ...card, padding: 0 }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, ...sectionTitle }}>
              <AlertTriangle size={15} /> Needs attention
            </div>
            {data.attention.map((a) => (
              <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
                <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: SEV_DOT[a.severity], flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={text.itemTitle}>{a.title}</div>
                    <div style={{ ...text.hint, marginTop: 2 }}>{a.description}</div>
                  </div>
                  {a.dueAt && <span style={{ fontSize: text.meta.fontSize, color: a.severity === "high" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                </div>
              </PortalLink>
            ))}
          </div>
        )}

        {data.findings.length === 0 ? (
          <div style={card}>
            <EmptyState
              title="No vulnerabilities recorded yet"
              detail="Connect Microsoft Defender and the platform reads every vulnerability it reports on your devices, then closes each one when Defender stops reporting it. No Defender? Import the CSV export from your vulnerability scanner instead."
              action={
                <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                  <PortalLink to="/connections" style={{ ...btnPrimary, textDecoration: "none" }}>Connect Microsoft Defender</PortalLink>
                  {can("resilience:write") && <button type="button" onClick={() => setImportOpen(true)} style={secondaryBtn}>Import scanner results</button>}
                </div>
              }
            />
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <select value={band} onChange={(e) => setParam("band", e.target.value)} style={{ ...input, width: "auto" }} aria-label="Priority">
                <option value="">All priorities</option>
                {BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={{ ...input, width: "auto" }} aria-label="Severity">
                <option value="">All severities</option>
                {SEVERITIES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
              <select value={sla} onChange={(e) => setParam("sla", e.target.value)} style={{ ...input, width: "auto" }} aria-label="Deadline">
                <option value="">Any deadline</option>
                {STANDING_FILTERS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <select value={source} onChange={(e) => setSource(e.target.value)} style={{ ...input, width: "auto" }} aria-label="Source">
                <option value="">All sources</option>
                <option value="defender">Defender</option>
                <option value="scanner">Scanner imports</option>
              </select>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search CVE, title or asset" style={{ ...input, width: 220, flex: "1 1 180px", maxWidth: 320 }} />
              <label style={{ display: "flex", alignItems: "center", gap: 6, ...text.hint, cursor: "pointer" }}>
                <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} /> Show resolved
              </label>
              <span style={{ ...text.hint, marginLeft: "auto" }}>{visible.length} of {showResolved ? data.findings.length : openCount}</span>
            </div>
            <div style={text.hint}>
              Defender findings close themselves when Defender stops reporting them. Scanner findings close on the next import that no longer reports them, or when you mark them fixed.
            </div>
            {rowError && <div style={{ ...text.body, color: theme.color.dangerText }}>{rowError}</div>}

            <div style={{ ...card, padding: 0, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={headRow}>
                  {["", "Priority", "Finding", "Asset", "Severity", "First seen", "Deadline", "Source"].map((h, i) => <th key={i} style={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {visible.map((f) => {
                    const open = expanded === f.id;
                    const b = BAND_STYLE[f.priority.band];
                    return (
                      <FindingRows key={f.id} f={f} open={open} bandStyle={b} scanners={data.scanners}
                        onToggle={() => setExpanded(open ? null : f.id)}
                        canAccept={can("risks:write")} canResolve={can("resilience:write")} tickets={tickets}
                        onException={() => setExceptionFor(f)} onRemoveException={() => removeException(f)} onResolve={() => setResolveFor(f)} />
                    );
                  })}
                  {visible.length === 0 && <tr><td colSpan={8} style={{ padding: 20, color: theme.color.muted }}>Nothing matches these filters.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20, alignItems: "start" }}>
          <SlaCard sla={data.sla} ceEnrolled={data.ceEnrolled} canEdit={can("org:admin")} onSaved={load} />
          <ImportHistory imports={data.imports} scanners={data.scanners} />
        </div>
      </div>

      {exceptionFor && <ExceptionModal finding={exceptionFor} onClose={() => setExceptionFor(null)} onSaved={() => { setExceptionFor(null); load(); }} />}
      {resolveFor && <ResolveModal finding={resolveFor} onClose={() => setResolveFor(null)} onSaved={() => { setResolveFor(null); load(); }} />}
      {importOpen && <ImportModal scanners={data.scanners} onClose={() => setImportOpen(false)} onImported={load} />}
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────────

function Tile({ label, value, sub, danger, onClick }: { label: string; value: string | number; sub?: string; danger?: boolean; onClick?: () => void }) {
  return (
    <div onClick={onClick} role={onClick ? "button" : undefined} style={{ ...card, padding: "14px 16px", height: "100%", cursor: onClick ? "pointer" : undefined, borderColor: danger ? theme.color.danger : undefined }}>
      <div style={text.statLabel}>{label}</div>
      <div style={{ ...text.stat, color: danger ? theme.color.dangerText : theme.color.heading, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function FindingRows({ f, open, bandStyle, scanners, onToggle, canAccept, canResolve, tickets, onException, onRemoveException, onResolve }: {
  f: Finding; open: boolean; bandStyle: { bg: string; color: string }; scanners: Record<string, string>; onToggle: () => void;
  canAccept: boolean; canResolve: boolean; tickets: TicketLinks; onException: () => void; onRemoveException: () => void; onResolve: () => void;
}) {
  const ticket = tickets.linkFor(f.id);
  const deadline = f.resolvedAt
    ? <span style={{ color: STANDING_COLOR.resolved }}>Fixed {fmtDay(f.resolvedAt)}</span>
    : f.standing === "excepted"
      ? <span style={{ color: STANDING_COLOR.excepted }}>Risk accepted until {fmtDateOnly(f.exceptionUntil)}</span>
      : f.dueAt
        ? <><div style={{ color: theme.color.text }}>{fmtDay(f.dueAt)}</div><div style={{ fontSize: text.meta.fontSize, color: STANDING_COLOR[f.standing], fontWeight: 600 }}>{relativeDue(f.dueAt)}</div></>
        : <span style={{ color: theme.color.muted }}>No deadline (severity unknown)</span>;
  const hasException = !!f.exceptionUntil && new Date(f.exceptionUntil).getTime() > Date.now();

  return (
    <>
      <tr onClick={onToggle} style={{ borderBottom: open ? "none" : "1px solid var(--c-border)", cursor: "pointer", opacity: f.resolvedAt ? 0.7 : 1 }}>
        <td style={{ ...td, paddingRight: 0, color: theme.color.muted }}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td style={td}>
          <span style={pill(bandStyle.bg, bandStyle.color)}>{f.priority.band}</span>
          <div style={{ ...text.meta, marginTop: 3 }}>score {f.priority.score}</div>
        </td>
        <td style={{ ...td, minWidth: 240 }}>
          <div style={{ fontWeight: 600, color: theme.color.text, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={text.ref}>{f.cveId}</span>
            {f.kev && <span style={pill(theme.color.dangerBg, theme.color.dangerText)}>Known exploited</span>}
            {f.epss != null && f.epss >= 0.01 && <span style={pill(theme.color.warningBg, theme.color.warningText)}>EPSS {Math.round(f.epss * 100)}%</span>}
          </div>
          <div style={{ ...text.hint, marginTop: 2 }}>{f.title}</div>
          {ticket && <div style={{ marginTop: 4 }}><TicketChip link={ticket} compact /></div>}
        </td>
        <td style={td}>
          <div style={{ color: theme.color.text }}>{f.asset.name}</div>
          <div style={text.meta}>{f.asset.criticality} criticality{f.asset.isEssentialFunction ? " · essential function" : ""}</div>
        </td>
        <td style={td}>{f.severity ? <SeverityPill value={f.severity} /> : <span style={{ color: theme.color.faint }}>—</span>}{f.cvss != null && <div style={{ ...text.meta, marginTop: 3 }}>CVSS {f.cvss.toFixed(1)}</div>}</td>
        <td style={{ ...td, color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDay(f.firstSeenAt)}</td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>{deadline}</td>
        <td style={{ ...td, color: theme.color.muted, whiteSpace: "nowrap" }}>{sourceLabel(f, scanners)}</td>
      </tr>
      {open && (
        <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
          <td />
          <td colSpan={7} style={{ padding: "0 14px 14px" }}>
            <div style={{ background: "var(--c-panelAlt)", borderRadius: 3, padding: 14 }}>
              {/* Fixed by its deadline, or the risk accepted on the record: nothing in between. */}
              {!f.resolvedAt && (
                <NextSteps items={hasException ? [
                  { done: !!f.exceptionReason?.trim(), label: "Record why the risk is accepted" },
                  { done: !!f.risk, label: "Link the risk register entry it is accepted under" },
                ] : [
                  { done: !!f.dueAt, label: "Set a deadline", hint: "The severity is unknown, so no deadline was set automatically." },
                  { done: !!f.remediationNote?.trim() || !!f.fixingKbId, label: "Record how it will be fixed" },
                  { done: false, label: f.standing === "breached" ? "Fix it, or accept the risk (past the deadline)" : "Fix it by the deadline, or accept the risk", urgent: f.standing === "breached" },
                ]} />
              )}
              <DetailSection title="Finding">
                <DetailFields fields={[
                  { label: "CVE", value: <span style={text.ref}>{f.cveId}</span> },
                  { label: "Title", value: f.title },
                  { label: "Product", value: f.productName },
                  { label: "Port", value: f.port },
                  { label: "Severity", value: f.severity ? `${f.severity[0].toUpperCase()}${f.severity.slice(1)}${f.cvss != null ? ` (CVSS ${f.cvss.toFixed(1)})` : ""}` : null },
                  { label: "Known exploited", value: f.kev ? "Yes" : "No" },
                  { label: "Exploit likelihood (EPSS)", value: f.epss != null ? `${Math.round(f.epss * 100)}%` : null },
                  { label: "Source", value: sourceLabel(f, scanners) },
                  { label: "Why this priority", value: `${f.priority.band}, score ${f.priority.score}: ${f.priority.reasons.join("; ")}.`, wide: true },
                ]} />
              </DetailSection>
              <DetailSection title="Asset">
                <DetailFields fields={[
                  { label: "Device", value: f.asset.name },
                  { label: "Criticality", value: `${f.asset.criticality}${f.asset.isEssentialFunction ? " · essential function" : ""}` },
                ]} />
              </DetailSection>
              <DetailSection title="Remediation">
                <DetailFields fields={[
                  { label: "First seen", value: fmtDay(f.firstSeenAt) },
                  { label: "Last seen", value: fmtDateTime(f.lastSeenAt) },
                  { label: "Deadline", value: f.dueAt ? `${fmtDay(f.dueAt)}${f.resolvedAt ? "" : ` (${relativeDue(f.dueAt)})`}` : null },
                  { label: "Fixed", value: f.resolvedAt ? fmtDay(f.resolvedAt) : null },
                  { label: "Fixing update", value: f.fixingKbId ? `KB${f.fixingKbId.replace(/^KB/i, "")}` : null },
                  { label: f.resolvedAt && f.source === "scanner" ? "Remediation note" : "How to fix", value: f.remediationNote, wide: true, multiline: true },
                  ...(hasException ? [
                    { label: "Risk accepted until", value: fmtDateOnly(f.exceptionUntil) },
                    { label: "Why the risk is accepted", value: f.exceptionReason, wide: true, multiline: true },
                  ] : []),
                  { label: hasException ? "Accepted under" : "Risk register entry", value: f.risk ? <PortalLink to={`/risk-register?risk=${encodeURIComponent(f.risk.id)}`} style={{ color: theme.color.accentText }}>{riskLabel(f.risk)}</PortalLink> : null, wide: true },
                ]} />
                {!f.resolvedAt && f.source === "defender" && <div style={{ ...text.meta, marginTop: 8 }}>This finding closes itself when Defender stops reporting it.</div>}
                {(ticket || !f.resolvedAt) && (
                  <div style={{ marginTop: 10 }}>
                    <TicketControl kind="vulnerability" subjectRef={f.id} links={tickets} canRaise={canResolve} />
                  </div>
                )}
              </DetailSection>
              {!f.resolvedAt && (canAccept || (canResolve && f.source === "scanner")) && (
                <RecordActions>
                  {canAccept && !hasException && <button type="button" onClick={(e) => { e.stopPropagation(); onException(); }} style={secondaryBtn}>Accept the risk until…</button>}
                  {canAccept && hasException && <button type="button" onClick={(e) => { e.stopPropagation(); onRemoveException(); }} style={dangerBtn}>Remove exception</button>}
                  {canResolve && f.source === "scanner" && <button type="button" onClick={(e) => { e.stopPropagation(); onResolve(); }} style={secondaryBtn}>Mark fixed</button>}
                </RecordActions>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function addDays(days: number): string {
  const d = new Date(Date.now() + days * 86_400_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function ExceptionModal({ finding, onClose, onSaved }: { finding: Finding; onClose: () => void; onSaved: () => void }) {
  const [until, setUntil] = useState(addDays(30));
  const [reason, setReason] = useState("");
  const [riskId, setRiskId] = useState(finding.risk?.id ?? "");
  const [risks, setRisks] = useState<RiskOption[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ risks: RiskOption[] }>("/risks").then((d) => setRisks(d.risks.filter((r) => r.status !== "closed"))).catch(() => setRisks([]));
  }, []);
  const chosen = risks?.find((r) => r.id === riskId) ?? null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 10) { setErr("Explain why the risk is accepted (at least a sentence)"); return; }
    if (!riskId) { setErr("Name the risk register entry that accepts this finding"); return; }
    setSaving(true); setErr(null);
    try {
      await api.post(`/vulnerabilities/findings/${finding.id}/exception`, { until: dateInputToIso(until, { endOfDay: true }), reason: reason.trim(), riskId });
      onSaved();
    } catch (e2) { setErr(apiErrorMessage(e2, "Could not record the exception")); setSaving(false); }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Accept the risk: ${finding.cveId}`} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gap: 14 }}>
        <div style={{ ...text.body, color: theme.color.muted, lineHeight: 1.5 }}>
          On <strong style={{ color: theme.color.text }}>{finding.asset.name}</strong>. While the exception stands, the finding does not count as past its deadline. It lasts a year at most; review it and renew it then. The acceptance is recorded in the activity log.
        </div>
        <Field label="Accepted until" help="One year at most">
          <input type="date" required value={until} min={addDays(1)} max={addDays(365)} onChange={(e) => setUntil(e.target.value)} style={input} />
        </Field>
        <Field label="Why the risk is accepted" help="For example: the service is isolated and is being replaced in March; a compensating control is in place.">
          <textarea required rows={4} value={reason} onChange={(e) => setReason(e.target.value)} style={textarea} />
        </Field>
        <Field label="Accepted under" help="The risk register entry whose owner accepted this risk: it needs an accept treatment with the owner and the date they accepted it (ISO 27001 6.1.3 f).">
          <select required value={riskId} onChange={(e) => setRiskId(e.target.value)} style={input}>
            <option value="">{risks ? "Choose a risk…" : "Loading risks…"}</option>
            {(risks ?? []).map((r) => <option key={r.id} value={r.id}>{riskLabel(r)}{acceptsRisk(r) ? "" : " — no recorded acceptance"}</option>)}
          </select>
        </Field>
        {chosen && !acceptsRisk(chosen) && (
          <div style={{ ...text.body, color: theme.color.warningText }}>
            This risk has no accept treatment with an acceptance date and an owner (the treatment's, or the risk's own). Record the acceptance on the risk first, then come back.
          </div>
        )}
        {err && <div style={{ ...text.body, color: theme.color.dangerText }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Record exception"}</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function ResolveModal({ finding, onClose, onSaved }: { finding: Finding; onClose: () => void; onSaved: () => void }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (note.trim().length < 3) { setErr("Say what was done"); return; }
    setSaving(true); setErr(null);
    try { await api.post(`/vulnerabilities/findings/${finding.id}/resolve`, { note: note.trim() }); onSaved(); }
    catch (e2) { setErr(apiErrorMessage(e2, "Could not mark it fixed")); setSaving(false); }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Mark fixed: ${finding.cveId}`} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gap: 14 }}>
        <div style={{ ...text.body, color: theme.color.muted, lineHeight: 1.5 }}>
          On <strong style={{ color: theme.color.text }}>{finding.asset.name}</strong>. Use this when the fix went in before the next scan. If the next import still reports it, it opens again.
        </div>
        <Field label="What was done">
          <textarea required rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} placeholder="For example: patched to 2.4.62 on 12 September, change CHG-1042" />
        </Field>
        {err && <div style={{ ...text.body, color: theme.color.dangerText }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Mark fixed"}</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Scanner import ────────────────────────────────────────────────────────────

interface Preview {
  scanner: string;
  rows: number;
  findings: number;
  skipped: number;
  errors: string[];
  hosts: Array<{ host: string; assetId: string | null; assetName: string | null; findings: number }>;
}

interface ImportResult {
  scanner: string;
  rows: number;
  findings: number;
  matchedHosts: number;
  unmatchedHosts: string[];
  created: number;
  refreshed: number;
  resolved: number;
  skipped: number;
  errors: string[];
}

function ImportModal({ scanners, onClose, onImported }: { scanners: Record<string, string>; onClose: () => void; onImported: () => void }) {
  const [fileName, setFileName] = useState("");
  const [csv, setCsv] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [assets, setAssets] = useState<Array<{ id: string; name: string }>>([]);
  const [hostMap, setHostMap] = useState<Record<string, string>>({});
  const [scannedAt, setScannedAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const onFile = (file: File | undefined) => {
    setErr(null); setPreview(null); setCsv(null); setHostMap({});
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) { setErr("The file is larger than 15 MB. Split the export and import each part."); return; }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsv(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => setErr("Could not read the file");
    reader.readAsText(file);
  };

  const runPreview = async () => {
    if (!csv) return;
    setBusy(true); setErr(null);
    try {
      const p = await api.post<Preview>("/vulnerabilities/imports/preview", { csv });
      setPreview(p);
      if (p.hosts.some((h) => !h.assetId) && assets.length === 0) {
        const r = await api.get<{ assets: Array<{ id: string; name: string; status: string }> }>("/resilience/assets").catch(() => ({ assets: [] }));
        setAssets(r.assets.filter((a) => a.status === "active").map((a) => ({ id: a.id, name: a.name })).sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (e) { setErr(apiErrorMessage(e, "Could not read that export")); }
    setBusy(false);
  };

  const runImport = async () => {
    if (!csv) return;
    setBusy(true); setErr(null);
    try {
      const map = Object.fromEntries(Object.entries(hostMap).filter(([, v]) => !!v));
      const r = await api.post<ImportResult>("/vulnerabilities/imports", {
        csv, fileName: fileName || "scan.csv",
        scannedAt: dateInputToIso(scannedAt),
        hostMap: map,
      });
      setResult(r);
      onImported();
    } catch (e) { setErr(apiErrorMessage(e, "Could not import the scan")); }
    setBusy(false);
  };

  const matched = preview?.hosts.filter((h) => h.assetId) ?? [];
  const unmatched = preview?.hosts.filter((h) => !h.assetId) ?? [];

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title="Import scanner results" onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 14 }}>
        {result ? (
          <>
            <div style={text.body}>Imported from {scanners[result.scanner] ?? result.scanner}.</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
              {([["New findings", result.created], ["Refreshed", result.refreshed], ["Resolved by this scan", result.resolved], ["Hosts not matched", result.unmatchedHosts.length]] as Array<[string, number]>).map(([l, v]) => (
                <div key={l} style={{ ...card, padding: "12px 14px" }}>
                  <div style={text.statLabel}>{l}</div>
                  <div style={{ ...text.stat, color: theme.color.heading, marginTop: 2 }}>{v}</div>
                </div>
              ))}
            </div>
            {result.unmatchedHosts.length > 0 && (
              <div style={text.hint}>
                Findings on these hosts were not recorded: {result.unmatchedHosts.slice(0, 20).join(", ")}{result.unmatchedHosts.length > 20 ? "…" : ""}. Add them to the asset register, or map them on the next import.
              </div>
            )}
            {result.errors.length > 0 && <div style={{ ...text.hint, color: theme.color.warningText }}>{result.errors.slice(0, 5).join(" ")}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end" }}><button type="button" onClick={onClose} style={btnPrimary}>Done</button></div>
          </>
        ) : (
          <>
            <div style={{ ...text.body, color: theme.color.muted, lineHeight: 1.5 }}>
              Supported exports: Tenable Nessus CSV, Qualys VM CSV, Greenbone OpenVAS CSV, or any CSV with host, cve, severity, cvss, title and port columns. Each host is matched to a device in your asset register. A later import from the same scanner closes what it no longer finds on the hosts it scanned.
            </div>
            <Field label="Export file (.csv, 15 MB at most)">
              <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} style={input} />
            </Field>
            {!preview && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
                <button type="button" disabled={!csv || busy} onClick={runPreview} style={{ ...btnPrimary, opacity: !csv || busy ? 0.6 : 1 }}>{busy ? "Reading…" : "Check the file"}</button>
              </div>
            )}

            {preview && (
              <>
                <div style={text.body}>
                  <strong>{scanners[preview.scanner] ?? preview.scanner}</strong> export: {preview.rows} rows, {preview.findings} findings on {preview.hosts.length} host{preview.hosts.length === 1 ? "" : "s"}{preview.skipped ? `, ${preview.skipped} row${preview.skipped === 1 ? "" : "s"} skipped (informational or no finding)` : ""}.
                </div>
                {preview.errors.length > 0 && <div style={{ ...text.hint, color: theme.color.warningText }}>{preview.errors.slice(0, 5).join(" ")}</div>}
                <div style={{ ...card, padding: 0, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                    <thead><tr style={headRow}>{["Host", "Findings", "Device in the asset register"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {unmatched.map((h) => (
                        <tr key={h.host} style={{ borderBottom: "1px solid var(--c-border)" }}>
                          <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>{h.host}</td>
                          <td style={{ ...td, color: theme.color.muted }}>{h.findings}</td>
                          <td style={td}>
                            <select value={hostMap[h.host] ?? ""} onChange={(e) => setHostMap({ ...hostMap, [h.host]: e.target.value })} style={{ ...input, minWidth: 200 }}>
                              <option value="">Not matched — leave out</option>
                              {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                      {matched.map((h) => (
                        <tr key={h.host} style={{ borderBottom: "1px solid var(--c-border)" }}>
                          <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>{h.host}</td>
                          <td style={{ ...td, color: theme.color.muted }}>{h.findings}</td>
                          <td style={{ ...td, color: theme.color.successText }}>{h.assetName ?? "Matched"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={text.hint}>
                  {matched.length} host{matched.length === 1 ? "" : "s"} matched, {unmatched.length} not. Findings on a host you leave unmatched are not recorded.
                </div>
                <Field label="Scan date (optional)" help="When the scan ran, if not today. Deadlines count from the date a finding was first seen.">
                  <input type="date" value={scannedAt} max={addDays(0)} onChange={(e) => setScannedAt(e.target.value)} style={{ ...input, maxWidth: 220 }} />
                </Field>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
                  <button type="button" disabled={busy || preview.findings === 0} onClick={runImport} style={{ ...btnPrimary, opacity: busy || preview.findings === 0 ? 0.6 : 1 }}>{busy ? "Importing…" : "Import"}</button>
                </div>
              </>
            )}
          </>
        )}
        {err && <div style={{ ...text.body, color: theme.color.dangerText }}>{err}</div>}
      </div>
    </ModalOverlay>
  );
}

function ImportHistory({ imports, scanners }: { imports: ImportRecord[]; scanners: Record<string, string> }) {
  return (
    <div style={{ ...card, padding: 0, overflowX: "auto", minWidth: 0 }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", ...sectionTitle }}>Import history</div>
      {imports.length === 0 ? (
        <div style={{ ...emptyFrame, margin: 14, ...text.body, color: theme.color.muted }}>No scanner exports imported yet.</div>
      ) : (
        // Fixed layout: the counts keep their widths and the file name takes what is left, cut
        // with an ellipsis, so the table fits the card instead of pushing the counts out of view.
        <table style={{ width: "100%", minWidth: 620, tableLayout: "fixed", borderCollapse: "collapse", fontSize: text.hint.fontSize }}>
          <colgroup>
            <col style={{ width: 136 }} /><col style={{ width: 110 }} /><col />
            <col style={{ width: 60 }} /><col style={{ width: 84 }} /><col style={{ width: 80 }} /><col style={{ width: 90 }} />
          </colgroup>
          <thead><tr style={headRow}>{["Imported", "Scanner", "File", "New", "Refreshed", "Resolved", "Unmatched"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {imports.map((i) => (
              <tr key={i.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                <td style={{ ...td, color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDay(i.createdAt)}{i.scannedAt && <div style={text.meta}>scanned {fmtDateOnly(i.scannedAt)}</div>}</td>
                <td style={{ ...td, color: theme.color.text }}>{scanners[i.scanner] ?? i.scanner}</td>
                {/* One line, cut with an ellipsis (full name on hover): "anywhere" let the table squeeze the column to a letter per line. */}
                <td style={{ ...td, color: theme.color.muted }}><div title={i.fileName} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.fileName}</div></td>
                <td style={{ ...td, color: theme.color.text }}>{i.created}</td>
                <td style={{ ...td, color: theme.color.text }}>{i.refreshed}</td>
                <td style={{ ...td, color: theme.color.text }}>{i.resolved}</td>
                <td style={{ ...td, color: (i.unmatchedHosts?.length ?? 0) > 0 ? theme.color.warningText : theme.color.muted }}>{i.unmatchedHosts?.length ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SlaCard({ sla, ceEnrolled, canEdit, onSaved }: { sla: Sla; ceEnrolled: boolean; canEdit: boolean; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<Severity, string>>({ critical: "", high: "", medium: "", low: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const start = () => { setDraft({ critical: String(sla.critical), high: String(sla.high), medium: String(sla.medium), low: String(sla.low) }); setErr(null); setEditing(true); };
  const cap = (s: Severity) => (ceEnrolled && (s === "critical" || s === "high") ? CE_MAX_DAYS : 365);

  const save = async () => {
    const body = {} as Record<Severity, number>;
    for (const s of SEVERITIES) {
      const n = Number(draft[s]);
      if (!Number.isInteger(n) || n < 1 || n > cap(s)) { setErr(`${s[0].toUpperCase() + s.slice(1)}: enter whole days between 1 and ${cap(s)}`); return; }
      body[s] = n;
    }
    setSaving(true); setErr(null);
    try { await api.put("/vulnerabilities/sla", body); setEditing(false); onSaved(); }
    catch (e) { setErr(apiErrorMessage(e, "Could not save the deadlines")); }
    setSaving(false);
  };

  return (
    <div style={{ ...card, padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div style={{ ...sectionTitle, display: "flex", alignItems: "center", gap: 6 }}><Clock size={14} /> Remediation deadlines</div>
        {canEdit && !editing && <button type="button" onClick={start} style={secondaryBtn}>Edit</button>}
      </div>
      <div style={{ ...text.hint, lineHeight: 1.5, marginBottom: 10 }}>
        Days from when a vulnerability is first seen to when it must be fixed.
        {ceEnrolled && <> <strong style={{ color: theme.color.text }}>Cyber Essentials requires critical and high updates within 14 days.</strong></>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10 }}>
        {SEVERITIES.map((s) => (
          <div key={s} style={{ display: "grid", gap: 4 }}>
            <div><SeverityPill value={s} /></div>
            {editing ? (
              <input type="number" min={1} max={cap(s)} value={draft[s]} onChange={(e) => setDraft({ ...draft, [s]: e.target.value })} style={input} aria-label={`${s} days`} />
            ) : (
              <div style={text.stat}>{sla[s]} <span style={{ ...text.hint, fontWeight: 400 }}>days</span></div>
            )}
          </div>
        ))}
      </div>
      {err && <div style={{ ...text.body, color: theme.color.dangerText, marginTop: 10 }}>{err}</div>}
      {editing && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button type="button" onClick={() => setEditing(false)} style={secondaryBtn}>Cancel</button>
          <button type="button" disabled={saving} onClick={save} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
        </div>
      )}
    </div>
  );
}
